/**
 * reseatSceneBoneBind — the SCENE half of the rotational unbind-adjust-rebind
 * (ADR-030; the kernel half is reseatBoneBind, and the run driver applies the
 * two together, never separately). Rotate a skeleton bone's BIND orientation
 * about a world axis through its own bind origin, its subtree carried
 * rigidly, while the mesh stays exactly where it is:
 *
 *   - the bone NODE's local rotation becomes parentBind⁻¹ · R · bind — the
 *     parent's frame is IBM-derived, pose-independent, the same source the
 *     recenter trusts;
 *   - EVERY subtree bone's inverse-bind matrix is recomputed as the exact
 *     inverse of its rotated bind world (origin orbits the reseated bone's
 *     origin, orientation pre-multiplied by R, scale untouched) — rest state
 *     composes boneWorld · IBM = I before and after, so no vertex moves;
 *   - descendant NODES are untouched: a rigid subtree keeps every local.
 *
 * Why not compensate the child like the recenter does (ADR-030 §12): a clip
 * overwrites a tracked child's local, so a compensation there is exactly as
 * durable as the clip's silence about that bone. Rotating the subtree gives
 * one answer whether or not a descendant is tracked.
 *
 * Every Skeleton that holds ANY bone of the subtree is treated — a clothed
 * subject's garment skin references the same bone objects with its own
 * inverse binds (Codex P2, #108), and a hair or hat skin may hold the Head
 * WITHOUT the Neck (Codex P1, #126): its Head bind still orbits the Neck's
 * origin, so its inverse bind must move too or it detaches at rest. The
 * bone's own bind frame comes from a skeleton that holds the bone; skeletons
 * that disagree about that bind are a broken file, refused.
 *
 * Fail-loud: unknown bone, a bone no skeleton carries an inverse bind for, no
 * skeleton carrying both the bone and its parent, a parent that is not a
 * Bone, a non-finite rotation — all throw. Caller bugs, not outcomes.
 *
 * The bone is resolved INSIDE the selected skeleton (`target.selectSkeleton`,
 * the one rule every door applies — Codex P1, #126 round three): a target
 * carrying two disjoint skeletons with duplicate joint names (mixamo-walking's
 * two X Bot skins are the fixture class) is reseated on the one the Document
 * chose, never on whichever bone of that name traversal meets first.
 *
 * Depends on: three, convert-rig (the selected-skeleton seam). Depended on
 * by: app session driver (run-document) and the app's in-place seating commit.
 */
import { Bone, Matrix4, Quaternion, Skeleton, Vector3, type Object3D } from 'three';
import { boneOfSelectedSkeleton, skinnedMeshesOfSelectedSkeleton } from './convert-rig.js';
import { AdapterError } from './errors.js';

export class SceneReseatError extends AdapterError {}

export interface SceneReseatReport {
  readonly bone: string;
  /** Subtree bones (skeleton members, excluding the bone itself) whose inverse binds moved. */
  readonly descendants: readonly string[];
  /** Distinct skeletons treated — every one holding any bone of the subtree. */
  readonly skeletons: number;
  /**
   * The rotation to LEFT-multiply onto any STORED rest local of `bone` so it
   * matches the node's new rest: local' = localPreRotation · local. Rests the
   * app captured before the surgery (pose projector seeds) transform by it.
   */
  readonly localPreRotation: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly w: number;
  };
}

interface BindFrame {
  readonly position: Vector3;
  readonly rotation: Quaternion;
  readonly scale: Vector3;
}

function bindFrameOf(skeleton: Skeleton, index: number, boneName: string): BindFrame {
  const ibm = skeleton.boneInverses[index];
  if (ibm === undefined) {
    throw new SceneReseatError(
      `skeleton has no inverse-bind matrix for "${boneName}" (index ${index}).`,
    );
  }
  const world = new Matrix4().copy(ibm).invert();
  const position = new Vector3();
  const rotation = new Quaternion();
  const scale = new Vector3();
  world.decompose(position, rotation, scale);
  return { position, rotation, scale };
}

/**
 * Rotate `boneName`'s bind by `radians` about the unit world `axis` through
 * the bone's bind origin, subtree rigid, inverse binds recomputed. See the
 * module header.
 */
export function reseatSceneBoneBind(
  root: Object3D,
  boneName: string,
  reseat: {
    readonly axis: { readonly x: number; readonly y: number; readonly z: number };
    readonly radians: number;
  },
  /** The Document's target identity + skeleton selector, as every other door passes them. */
  options: { readonly label: string; readonly selectSkeleton?: string },
): SceneReseatReport {
  if (!Number.isFinite(reseat.radians)) {
    throw new SceneReseatError(
      `cannot reseat "${boneName}" — the angle is not finite (${reseat.radians}).`,
    );
  }
  const axis = new Vector3(reseat.axis.x, reseat.axis.y, reseat.axis.z);
  if (!(axis.lengthSq() > 0) || !Number.isFinite(axis.lengthSq())) {
    throw new SceneReseatError(`cannot reseat "${boneName}" — the axis has no direction.`);
  }
  const R = new Quaternion().setFromAxisAngle(axis.normalize(), reseat.radians);

  // The selected skeleton's meshes (one group → no selector needed; several →
  // the selector, or MultipleSkeletonsError from the shared seam), and the
  // bone by name INSIDE that group.
  const meshes = skinnedMeshesOfSelectedSkeleton(root, options);
  if (meshes.length === 0) {
    throw new SceneReseatError(`cannot reseat "${boneName}" — the scene has no skinned mesh.`);
  }
  const node = boneOfSelectedSkeleton(root, boneName, options);
  if (node === undefined) {
    throw new SceneReseatError(
      `cannot reseat "${boneName}" — not a bone of the selected skeleton ("${options.label}").`,
    );
  }
  const parent = node.parent;
  if (!(parent instanceof Bone)) {
    throw new SceneReseatError(
      `cannot reseat "${boneName}" — its parent is not a skeleton bone (roots are out of scope; ` +
        `a neck always hangs from a spine).`,
    );
  }

  // The rigid body: the bone and every Bone below it, by object identity.
  const subtree = new Set<Object3D>();
  node.traverse((n) => {
    if (n instanceof Bone) subtree.add(n);
  });

  // Every skeleton of the selected group that holds ANY subtree bone, once
  // each; the ones that hold the reseated bone itself are the candidate
  // bind-frame sources.
  const skeletons: Skeleton[] = [];
  const holders: Skeleton[] = [];
  for (const mesh of meshes) {
    const skeleton = mesh.skeleton;
    if (skeletons.includes(skeleton)) continue;
    if (!skeleton.bones.some((b) => subtree.has(b))) continue;
    skeletons.push(skeleton);
    if (skeleton.bones.includes(node)) holders.push(skeleton);
  }
  if (holders.length === 0) {
    throw new SceneReseatError(
      `cannot reseat "${boneName}" — no skeleton carries an inverse bind for it.`,
    );
  }

  // The bind-frame SOURCE: a holder that carries the parent too, so the node's
  // new local can be decomposed against an IBM-derived parent frame. Not
  // "the first holder" — a collar skin holding the Neck but not the Spine,
  // traversed before the body, would have refused the whole surgery
  // (Sol, #126). Every holder is still cross-checked against the source.
  const source = holders.find((s) => s.bones.includes(parent));
  if (source === undefined) {
    throw new SceneReseatError(
      `cannot reseat "${boneName}" — no skeleton carries inverse binds for both it and its parent ` +
        `"${parent.name}" (${holders.length} hold the bone alone).`,
    );
  }
  const bind = bindFrameOf(source, source.bones.indexOf(node), boneName);
  for (const skeleton of holders) {
    if (skeleton === source) continue;
    const other = bindFrameOf(skeleton, skeleton.bones.indexOf(node), boneName);
    if (
      other.position.distanceTo(bind.position) > 1e-4 ||
      other.rotation.angleTo(bind.rotation) > 1e-4
    ) {
      throw new SceneReseatError(
        `cannot reseat "${boneName}" — two skeletons disagree about its bind ` +
          `(${other.position.distanceTo(bind.position).toExponential(2)} apart, ` +
          `${((other.rotation.angleTo(bind.rotation) * 180) / Math.PI).toFixed(3)}° off); the file is inconsistent.`,
      );
    }
  }
  const parentBind = bindFrameOf(source, source.bones.indexOf(parent), parent.name);

  // --- the node: parentBind⁻¹ · R · bind, pose-independent ---
  const newWorldRotation = R.clone().multiply(bind.rotation);
  node.quaternion.copy(parentBind.rotation).invert().multiply(newWorldRotation).normalize();
  node.updateMatrix();
  const localPreRotation = parentBind.rotation
    .clone()
    .invert()
    .multiply(R)
    .multiply(parentBind.rotation)
    .normalize();

  // --- every subtree inverse bind, in every skeleton that holds the bone ---
  const origin = bind.position;
  const descendants = new Set<string>();
  for (const skeleton of skeletons) {
    for (let i = 0; i < skeleton.bones.length; i += 1) {
      const member = skeleton.bones[i]!;
      if (!subtree.has(member)) continue;
      const frame = bindFrameOf(skeleton, i, member.name);
      const position = frame.position.clone().sub(origin).applyQuaternion(R).add(origin);
      const rotation = R.clone().multiply(frame.rotation);
      skeleton.boneInverses[i] = new Matrix4().compose(position, rotation, frame.scale).invert();
      if (member !== node) descendants.add(member.name);
    }
  }
  root.updateMatrixWorld(true);

  return {
    bone: boneName,
    descendants: [...descendants],
    skeletons: skeletons.length,
    localPreRotation: {
      x: localPreRotation.x,
      y: localPreRotation.y,
      z: localPreRotation.z,
      w: localPreRotation.w,
    },
  };
}
